import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage } from '../../api';
import { employeeService, type Employee } from '../../api/Employee';
import { fuelingService, type Fueling } from '../../api/fuelings';
import { providerService, type Provider } from '../../api/provedor';
import { vehicleService, type Vehicle } from '../../api/vehicles';
import { useAuth } from '../../context/AuthContext';
import {
  Fuel,
  RefreshCw,
  Search,
  Plus,
  Car,
  User,
  Calendar,
  Droplets,
  DollarSign,
  Factory,
  CircleCheck,
  CircleX,
  Edit2,
  Eye,
  Trash2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Gauge,
  Image as ImageIcon,
  X,
  CheckCircle2,
  Loader2,
  Clock,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Badge,
  Input,
  Select,
  Label,
  Modal,
  ConfirmModal,
} from '../../components/ui/SharedLayout';
type TipoCombustivel = 'Gasolina' | 'Etanol' | 'Diesel' | 'Flex' | 'GNV';
type FuelingStatus = 'pendente' | 'aprovado' | 'rejeitado' | 'finalizado';

const PRECOS_COMBUSTIVEL: Record<TipoCombustivel, number> = {
  Gasolina: 5.6,
  Etanol: 4.2,
  Diesel: 4.8,
  Flex: 4.9,
  GNV: 3.5,
};

type FuelingWithStatus = Omit<Fueling, 'status'> & {
  status: FuelingStatus;
};

interface FuelingFormState {
  veiculoId: string;
  motoristaId: string;
  data: string;
  tipoCombustivel: TipoCombustivel | '';
  quantidade: number;
  valorTotal: number;
  kmAtual: number;
  providerId: string;
}

const ITEMS_PER_PAGE = 20;

type FormErrors = Partial<Record<keyof FuelingFormState, string>>;

type FieldWrapperProps = {
  children: ReactNode;
  error?: string;
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
};

const FieldWrapper = ({ children, error, label, htmlFor, required }: FieldWrapperProps) => (
  <div className="flex flex-col gap-1">
    {label ? (
      <Label htmlFor={htmlFor} className="mb-0.5">
        <span className="inline-flex items-center gap-0.5">
          {label}
          {required ? <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span> : null}
        </span>
      </Label>
    ) : null}
    {children}
    {error ? (
      <div className="flex items-center gap-1 text-xs text-rose-600">
        <AlertCircle className="h-3 w-3 flex-shrink-0" />
        <span className="leading-tight">{error}</span>
      </div>
    ) : null}
  </div>
);

const defaultFormState: FuelingFormState = {
  veiculoId: '',
  motoristaId: '',
  data: '',
  tipoCombustivel: '',
  quantidade: 0,
  valorTotal: 0,
  kmAtual: 0,
  providerId: '',
};

const normalizeStatus = (status?: string): FuelingStatus => {
  switch (status) {
    case 'approved':
    case 'aprovado':
      return 'aprovado';
    case 'rejected':
    case 'rejeitado':
      return 'rejeitado';
    case 'finalizado':
      return 'finalizado';
    case 'blocked':
    case 'pending':
    case 'pendente':
    default:
      return 'pendente';
  }
};

const statusBadgeTone: Record<FuelingStatus, 'amber' | 'green' | 'red' | 'slate'> = {
  pendente: 'amber',
  aprovado: 'green',
  rejeitado: 'red',
  finalizado: 'slate',
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const buildFuelingTime = (date: string, fallback?: string) => {
  if (date) {
    return new Date(`${date}T12:00:00`).toISOString();
  }
  return fallback;
};

const buildFormStateFromFueling = (
  fueling: FuelingWithStatus,
): FuelingFormState => ({
  veiculoId: String(fueling.vehicle_id),
  motoristaId: String(fueling.employee_id),
  data: fueling.fueling_time ? fueling.fueling_time.split('T')[0] : '',
  tipoCombustivel: (fueling.fuel_type as TipoCombustivel) || '',
  quantidade: Number(fueling.amount) || 0,
  valorTotal: Number(fueling.total_cost) || 0,
  kmAtual: Number(fueling.odometer_reading) || 0,
  providerId: String(fueling.provider_id),
});

const Abastecimento = () => {
  useAuth();
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [abastecimentos, setAbastecimentos] = useState<FuelingWithStatus[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});

  const [formState, setFormState] = useState<FuelingFormState>(defaultFormState);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingFueling, setEditingFueling] = useState<FuelingWithStatus | null>(null);
  const [selectedFueling, setSelectedFueling] = useState<FuelingWithStatus | null>(null);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [finishingFuelingId, setFinishingFuelingId] = useState<number | null>(null);
  const [boletoFile, setBoletoFile] = useState<File | null>(null);
  const [boletoPreview, setBoletoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [fuelingParaExcluir, setFuelingParaExcluir] = useState<FuelingWithStatus | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const showError = useCallback((msg: string) => setToast({ type: 'error', message: msg }), []);
  const showSuccess = useCallback((msg: string) => setToast({ type: 'success', message: msg }), []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [fuelings, loadedVehicles, loadedEmployees, loadedProviders] = await Promise.all([
        fuelingService.getAllFuelings(),
        vehicleService.getAllVehicles(),
        employeeService.getAllEmployees(),
        providerService.getAll(),
      ]);

      setAbastecimentos(
        fuelings
          .map((fueling) => ({
            ...fueling,
            status: normalizeStatus(fueling.status),
          }))
          .sort((a, b) => b.id - a.id),
      );
      setVehicles(loadedVehicles);
      setEmployees(loadedEmployees);
      setProviders(loadedProviders);
    } catch (fetchError) {
      console.error(fetchError);
      showError('Erro ao carregar os abastecimentos.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!boletoFile) {
      setBoletoPreview(null);
      return;
    }
    const url = URL.createObjectURL(boletoFile);
    setBoletoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [boletoFile]);

  const vehicleMap = useMemo(
    () => new Map(vehicles.map((vehicle) => [vehicle.id, vehicle])),
    [vehicles],
  );
  const employeeMap = useMemo(
    () => new Map(employees.map((employee) => [employee.id, employee])),
    [employees],
  );
  const providerMap = useMemo(
    () => new Map(providers.map((provider) => [provider.id, provider])),
    [providers],
  );

  const totalFiltrosAplicados = useMemo(
    () => Object.values(appliedFilters).reduce((acc, arr) => acc + arr.length, 0),
    [appliedFilters],
  );

  const filteredAbastecimentos = useMemo(() => {
    let list = abastecimentos.filter((fueling) => fueling.status !== 'finalizado');

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter((fueling) => {
        const vehicle = vehicleMap.get(fueling.vehicle_id);
        const employee = employeeMap.get(fueling.employee_id);
        const provider = providerMap.get(fueling.provider_id);

        return (
          fueling.fuel_type.toLowerCase().includes(term) ||
          fueling.amount.toString().includes(term) ||
          fueling.total_cost.toString().includes(term) ||
          vehicle?.brand?.toLowerCase().includes(term) ||
          vehicle?.description?.toLowerCase().includes(term) ||
          vehicle?.license_plate?.toLowerCase().includes(term) ||
          employee?.name?.toLowerCase().includes(term) ||
          provider?.name?.toLowerCase().includes(term)
        );
      });
    }

    if (totalFiltrosAplicados > 0) {
      list = list.filter((f) => {
        if (appliedFilters.status?.length) {
          if (!appliedFilters.status.includes(f.status)) return false;
        }
        if (appliedFilters.fuel_type?.length) {
          if (!appliedFilters.fuel_type.includes(f.fuel_type)) return false;
        }
        return true;
      });
    }

    return list;
  }, [abastecimentos, employeeMap, providerMap, searchTerm, vehicleMap, appliedFilters, totalFiltrosAplicados]);

  const totalPaginas = Math.max(1, Math.ceil(filteredAbastecimentos.length / ITEMS_PER_PAGE));
  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [currentPage, totalPaginas]);

  const pageRange = useMemo(() => {
    const total = filteredAbastecimentos.length;
    if (total === 0) return { start: 0, end: 0, total: 0 };
    const start = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const end = Math.min(currentPage * ITEMS_PER_PAGE, total);
    return { start, end, total };
  }, [currentPage, filteredAbastecimentos.length]);

  const abastecimentosPaginados = useMemo(
    () =>
      filteredAbastecimentos.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
      ),
    [currentPage, filteredAbastecimentos],
  );

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPaginas <= 7) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push('ellipsis');
    const startWin = Math.max(2, currentPage - 1);
    const endWin = Math.min(totalPaginas - 1, currentPage + 1);
    for (let i = startWin; i <= endWin; i++) pages.push(i);
    if (currentPage < totalPaginas - 2) pages.push('ellipsis');
    pages.push(totalPaginas);
    return pages;
  };

  const resetForm = () => {
    setFormState(defaultFormState);
    setFormErrors({});
  };

  const openCreateModal = () => {
    resetForm();
    setEditingFueling(null);
    setIsFormModalOpen(true);
  };

  const openEditModal = (fueling: FuelingWithStatus) => {
    setEditingFueling(fueling);
    setFormState(buildFormStateFromFueling(fueling));
    setFormErrors({});
    setSelectedFueling(null);
    setIsFormModalOpen(true);
  };

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setEditingFueling(null);
    resetForm();
  };

  const closeDetailsModal = () => {
    setSelectedFueling(null);
  };

  const closeFinishModal = () => {
    setIsFinishModalOpen(false);
    setFinishingFuelingId(null);
    setBoletoFile(null);
    setBoletoPreview(null);
  };

  const handleClearFilters = () => {
    setAppliedFilters({});
    setCurrentPage(1);
  };

  const handleFormChange = (
    field: keyof FuelingFormState,
    value: string | number,
  ) => {
    setFormState((current) => {
      const next = { ...current, [field]: value };
      if (field === 'tipoCombustivel' || field === 'quantidade') {
        const tipo = (field === 'tipoCombustivel' ? value : next.tipoCombustivel) as
          | TipoCombustivel
          | '';
        const quantidade =
          field === 'quantidade' ? Number(value) || 0 : Number(next.quantidade) || 0;
        next.valorTotal = tipo ? quantidade * PRECOS_COMBUSTIVEL[tipo] : 0;
      }
      return next;
    });
    if (formErrors[field]) setFormErrors((cur) => ({ ...cur, [field]: undefined }));
  };

  const validateFuelingForm = () => {
    const errors: FormErrors = {};
    if (!formState.veiculoId) errors.veiculoId = 'Selecione um veículo';
    if (!formState.motoristaId) errors.motoristaId = 'Selecione um motorista';
    if (!formState.data) errors.data = 'Informe a data do abastecimento';
    if (!formState.tipoCombustivel) errors.tipoCombustivel = 'Selecione o tipo de combustível';
    if (!formState.providerId) errors.providerId = 'Selecione um fornecedor';
    if (!Number.isFinite(formState.quantidade) || formState.quantidade <= 0) {
      errors.quantidade = 'Informe uma quantidade maior que zero';
    }
    if (!Number.isFinite(formState.valorTotal) || formState.valorTotal <= 0) {
      errors.valorTotal = 'Valor total precisa ser maior que zero';
    }
    if (!Number.isFinite(formState.kmAtual) || formState.kmAtual < 0) {
      errors.kmAtual = 'Informe uma quilometragem válida';
    }
    if (formState.kmAtual > 2147483647) {
      errors.kmAtual = 'Quilometragem muito alta (máx 2.147.483.647)';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveFueling = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateFuelingForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: Number(formState.veiculoId),
        employee_id: Number(formState.motoristaId),
        provider_id: Number(formState.providerId),
        fuel_type: formState.tipoCombustivel,
        amount: Number(formState.quantidade),
        cost_per_liter: PRECOS_COMBUSTIVEL[formState.tipoCombustivel as TipoCombustivel],
        total_cost: Number(formState.valorTotal),
        odometer_reading: formState.kmAtual || undefined,
        invoice_number: undefined,
        notes: undefined,
        fueling_time: buildFuelingTime(formState.data),
      };

      if (editingFueling) {
        const updatedFueling = await fuelingService.updateFueling(editingFueling.id, payload);
        setAbastecimentos((current) =>
          current
            .map((item) =>
              item.id === editingFueling.id
                ? {
                    ...item,
                    ...updatedFueling,
                    status: normalizeStatus(updatedFueling.status || item.status),
                  }
                : item,
            )
            .sort((a, b) => b.id - a.id),
        );
        setSelectedFueling((current) =>
          current && current.id === editingFueling.id
            ? {
                ...current,
                ...updatedFueling,
                status: normalizeStatus(updatedFueling.status || current.status),
              }
            : current,
        );
        showSuccess('Abastecimento atualizado com sucesso.');
      } else {
        const createdFueling = await fuelingService.createFueling(payload);
        setAbastecimentos((current) =>
          [
            { ...createdFueling, status: normalizeStatus(createdFueling.status) },
            ...current,
          ].sort((a, b) => b.id - a.id),
        );
        showSuccess('Abastecimento registrado com sucesso.');
      }

      closeFormModal();
    } catch (saveError) {
      console.error(saveError);
      showError(
        getApiErrorMessage(
          saveError,
          editingFueling
            ? 'Erro ao atualizar abastecimento. Verifique sua conexão e tente novamente.'
            : 'Erro ao registrar abastecimento. Verifique sua conexão e tente novamente.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const abrirConfirmacaoExclusao = (fueling: FuelingWithStatus) => {
    setFuelingParaExcluir(fueling);
    setIsDeleteConfirmOpen(true);
  };

  const cancelarExclusao = () => {
    setIsDeleteConfirmOpen(false);
    setFuelingParaExcluir(null);
  };

  const confirmarExclusao = async () => {
    if (!fuelingParaExcluir) return;
    setDeleteLoading(true);
    try {
      await fuelingService.deleteFueling(fuelingParaExcluir.id);
      setAbastecimentos((current) => current.filter((item) => item.id !== fuelingParaExcluir.id));
      setSelectedFueling((current) =>
        current && current.id === fuelingParaExcluir.id ? null : current,
      );
      showSuccess('Abastecimento excluído com sucesso.');
      cancelarExclusao();
    } catch (deleteError) {
      console.error(deleteError);
      showError('Erro ao excluir abastecimento.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleStatusChange = async (
    fueling: FuelingWithStatus,
    newStatus: 'aprovado' | 'rejeitado',
  ) => {
    try {
      const updatedFueling = await fuelingService.updateFueling(fueling.id, {
        vehicle_id: fueling.vehicle_id,
        employee_id: fueling.employee_id,
        provider_id: fueling.provider_id,
        fuel_type: fueling.fuel_type,
        amount: fueling.amount,
        cost_per_liter: fueling.cost_per_liter,
        total_cost: fueling.total_cost,
        odometer_reading: fueling.odometer_reading,
        invoice_number: fueling.invoice_number,
        notes: fueling.notes,
        fueling_time: fueling.fueling_time,
        status: newStatus,
      });

      const normalized = normalizeStatus(updatedFueling.status || newStatus);
      setAbastecimentos((current) =>
        current.map((item) =>
          item.id === fueling.id ? { ...item, ...updatedFueling, status: normalized } : item,
        ),
      );
      setSelectedFueling((current) =>
        current && current.id === fueling.id
          ? { ...current, ...updatedFueling, status: normalized }
          : current,
      );
      showSuccess(
        `Abastecimento ${newStatus === 'aprovado' ? 'aprovado' : 'rejeitado'} com sucesso.`,
      );
    } catch (statusError) {
      console.error(statusError);
      showError('Erro ao atualizar o status do abastecimento.');
    }
  };

  const openFinishModal = (fuelingId: number) => {
    setFinishingFuelingId(fuelingId);
    setIsFinishModalOpen(true);
  };

  const handleFinishFueling = async () => {
    if (!boletoFile || finishingFuelingId === null) {
      showError('Selecione a foto do boleto para finalizar.');
      return;
    }

    const fueling = abastecimentos.find((item) => item.id === finishingFuelingId);
    if (!fueling) {
      closeFinishModal();
      return;
    }

    setSubmitting(true);
    try {
      const boletoBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target?.result as string);
        reader.onerror = () => reject(new Error('Falha ao ler boleto'));
        reader.readAsDataURL(boletoFile);
      });

      await fuelingService.updateFueling(fueling.id, {
        vehicle_id: fueling.vehicle_id,
        employee_id: fueling.employee_id,
        provider_id: fueling.provider_id,
        fuel_type: fueling.fuel_type,
        amount: fueling.amount,
        cost_per_liter: fueling.cost_per_liter,
        total_cost: fueling.total_cost,
        odometer_reading: fueling.odometer_reading,
        invoice_number: fueling.invoice_number,
        notes: fueling.notes,
        fueling_time: fueling.fueling_time,
        status: 'finalizado',
        boleto_photo: boletoBase64,
      });

      setAbastecimentos((current) => current.filter((item) => item.id !== fueling.id));
      setSelectedFueling((current) =>
        current && current.id === fueling.id ? null : current,
      );
      showSuccess('Abastecimento finalizado e enviado ao histórico.');
      closeFinishModal();
    } catch (finishError) {
      console.error(finishError);
      showError('Erro ao finalizar abastecimento.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatusBadge = (status: FuelingStatus) => (
    <Badge tone={statusBadgeTone[status]} className="!text-[12px]">
      {status === 'pendente' && <Clock className="h-3 w-3 stroke-[2]" />}
      {status === 'aprovado' && <CheckCircle2 className="h-3 w-3 stroke-[2]" />}
      {status === 'rejeitado' && <AlertCircle className="h-3 w-3 stroke-[2]" />}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );

  return (
    <div className="relative">
      <PageHeader title="Abastecimentos" hideTitleInBody />

      {toast ? (
        <div
          className={`fixed top-5 right-5 z-[80] inline-flex items-center gap-2.5 rounded-xl px-4.5 py-3 text-[13px] font-semibold text-white shadow-[0_10px_28px_-12px_rgba(15,23,42,0.45)] ring-1 ring-white/15 backdrop-blur ${
            toast.type === 'success'
              ? 'bg-gradient-to-br from-emerald-500 via-emerald-500 to-green-600'
              : 'bg-gradient-to-br from-rose-500 via-rose-500 to-red-600'
          }`}
          role="status"
        >
          {toast.type === 'success' ? (
            <CheckCircle2 className="h-4.5 w-4.5 stroke-[2]" />
          ) : (
            <AlertCircle className="h-4.5 w-4.5 stroke-[2]" />
          )}
          <span className="leading-tight">{toast.message}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="ml-1.5 inline-flex h-6 w-6 items-center justify-center rounded-md hover:bg-white/15 transition-colors"
            aria-label="Fechar aviso"
          >
            <X className="h-3.5 w-3.5 stroke-[2]" />
          </button>
        </div>
      ) : null}

      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[15px] font-semibold leading-tight text-blue-600">
              Gestão de abastecimentos
            </p>
            <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold leading-[1.15] tracking-tight text-slate-900">
              Registre, aprove e finalize os abastecimentos.
              <span className="ml-1.5 text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                Ao finalizar com boleto, o registro vai para o histórico.
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {totalFiltrosAplicados > 0 ? (
              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                <X className="h-4 w-4 stroke-[2]" />
                Limpar filtros
              </button>
            ) : null}
            <button
              type="button"
              onClick={loadData}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
            >
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </button>
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 text-[13px] font-semibold text-white shadow-[0_1px_2px_0_rgba(15,23,42,0.06),0_1px_3px_0_rgba(37,99,235,0.2)] transition-all hover:bg-blue-700 hover:shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)] focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:ring-offset-1 disabled:pointer-events-none disabled:opacity-60"
            >
              <Plus className="h-4 w-4 stroke-[2]" />
              Novo Abastecimento
            </button>
          </div>
        </div>

        <SectionCard>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white shadow-[0_6px_16px_-8px_rgba(249,115,22,0.6)]">
                <Fuel className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Abastecimentos ativos
                </h2>
                <p className="text-[12px] font-medium text-slate-500">
                  {pageRange.total > 0
                    ? `${pageRange.start} a ${pageRange.end} de ${pageRange.total} abastecimentos listados (ativos cadastrados: ${abastecimentos.filter((f) => f.status !== 'finalizado').length})`
                    : 'Nenhum abastecimento ativo listado'}
                </p>
              </div>
            </div>
            <div className="relative w-full sm:!w-[320px] lg:!w-[360px]">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                <Search className="h-[17px] w-[17px] stroke-[1.8]" />
              </div>
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Buscar veículo, motorista, fornecedor ou combustível..."
                className="pl-10"
              />
            </div>
          </CardHeader>

          <CardBody>
            <ModernTable>
              <Thead>
                <Tr>
                  <Th>Veículo</Th>
                  <Th>Motorista</Th>
                  <Th>Data</Th>
                  <Th>Combustível</Th>
                  <Th>Qtd</Th>
                  <Th>Valor Total</Th>
                  <Th>Fornecedor</Th>
                  <Th>Status</Th>
                  <Th>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-5 py-14 text-center text-sm font-medium text-slate-500"
                    >
                      Carregando abastecimentos...
                    </td>
                  </tr>
                ) : filteredAbastecimentos.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-5 py-14 text-center text-sm font-medium text-slate-500"
                    >
                      {searchTerm || totalFiltrosAplicados > 0
                        ? 'Nenhum abastecimento corresponde aos filtros aplicados.'
                        : 'Nenhum abastecimento ativo encontrado.'}
                    </td>
                  </tr>
                ) : (
                  abastecimentosPaginados.map((fueling) => {
                    const vehicle = vehicleMap.get(fueling.vehicle_id);
                    const employee = employeeMap.get(fueling.employee_id);
                    const provider = providerMap.get(fueling.provider_id);
                    const dataFormatada = formatDate(fueling.fueling_time);
                    return (
                      <Tr key={fueling.id}>
                        <Td>
                          <div className="min-w-0 w-full max-w-full overflow-hidden">
                            <div className="flex items-center gap-3 min-w-0 w-full max-w-full">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-sky-500 via-blue-500 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(59,130,246,0.6)]">
                                <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1 max-w-full">
                                <div
                                  className="truncate font-semibold text-slate-900 w-full"
                                  title={
                                    vehicle
                                      ? `${vehicle.brand}${vehicle.description ? ` · ${vehicle.description}` : ''}`
                                      : `Veículo #${fueling.vehicle_id}`
                                  }
                                >
                                  {vehicle ? vehicle.brand : `Veículo #${fueling.vehicle_id}`}
                                </div>
                                <div
                                  className="mt-0.5 text-[12px] text-slate-500 truncate w-full font-mono"
                                  title={vehicle?.license_plate || '-'}
                                >
                                  {vehicle?.license_plate || '-'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div className="min-w-0 w-full max-w-full overflow-hidden">
                            <div className="flex items-center gap-3 min-w-0 w-full max-w-full">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-indigo-500 via-violet-500 to-indigo-700 text-white shadow-[0_6px_16px_-8px_rgba(99,102,241,0.55)]">
                                <User className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1 max-w-full">
                                <div
                                  className="truncate font-semibold text-slate-900 w-full"
                                  title={employee?.name || `Motorista #${fueling.employee_id}`}
                                >
                                  {employee?.name || `Motorista #${fueling.employee_id}`}
                                </div>
                                <div
                                  className="mt-0.5 text-[12px] text-slate-500 truncate w-full"
                                  title={employee?.cpf || '-'}
                                >
                                  CPF {employee?.cpf || '-'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div className="min-w-0 w-full max-w-full overflow-hidden">
                            <span
                              className="block truncate font-medium text-slate-700 tabular-nums whitespace-nowrap w-full"
                              title={dataFormatada}
                            >
                              {dataFormatada}
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <div className="min-w-0 w-full max-w-full overflow-hidden">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Droplets className="h-4 w-4 stroke-[1.8] text-blue-500 shrink-0" />
                              <span
                                className="block truncate font-medium text-slate-700 w-full"
                                title={fueling.fuel_type}
                              >
                                {fueling.fuel_type}
                              </span>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div className="min-w-0 w-full max-w-full overflow-hidden">
                            <span
                              className="block truncate font-medium text-slate-700 tabular-nums whitespace-nowrap w-full"
                              title={`${fueling.amount} litros`}
                            >
                              {fueling.amount}L
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <div className="min-w-0 w-full max-w-full overflow-hidden">
                            <span
                              className="block truncate font-semibold text-slate-900 tabular-nums whitespace-nowrap w-full"
                              title={formatCurrency(fueling.total_cost)}
                            >
                              {formatCurrency(fueling.total_cost)}
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <div className="min-w-0 w-full max-w-full overflow-hidden">
                            <div className="flex items-center gap-2 min-w-0 w-full">
                              <Factory className="h-4 w-4 stroke-[1.8] text-slate-400 shrink-0" />
                              <span
                                className="block truncate font-medium text-slate-700 w-full"
                                title={provider?.name || `Fornecedor #${fueling.provider_id}`}
                              >
                                {provider?.name || `Fornecedor #${fueling.provider_id}`}
                              </span>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div className="min-w-0 w-full max-w-full">
                            {renderStatusBadge(fueling.status)}
                          </div>
                        </Td>
                        <Td>
                          <div className="flex flex-col items-start gap-1.5 whitespace-nowrap">
                            <div className="inline-flex items-center gap-1.5">
                              {fueling.status === 'pendente' && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleStatusChange(fueling, 'aprovado')}
                                    className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-50 px-2.5 text-[12px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:ring-offset-1"
                                    title="Aprovar abastecimento"
                                  >
                                    <CircleCheck className="h-3.5 w-3.5 stroke-[2]" />
                                    Aprovar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleStatusChange(fueling, 'rejeitado')}
                                    className="inline-flex h-8 items-center gap-1 rounded-lg bg-rose-50 px-2.5 text-[12px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-500/40 focus:ring-offset-1"
                                    title="Rejeitar abastecimento"
                                  >
                                    <CircleX className="h-3.5 w-3.5 stroke-[2]" />
                                    Rejeitar
                                  </button>
                                </>
                              )}
                              {fueling.status === 'aprovado' && (
                                <button
                                  type="button"
                                  onClick={() => openFinishModal(fueling.id)}
                                  className="inline-flex h-8 items-center gap-1 rounded-lg bg-blue-50 px-2.5 text-[12px] font-semibold text-blue-700 ring-1 ring-inset ring-blue-200 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:ring-offset-1"
                                  title="Finalizar abastecimento com boleto"
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5 stroke-[2]" />
                                  Finalizar
                                </button>
                              )}
                            </div>
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => openEditModal(fueling)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                                title="Editar abastecimento"
                                aria-label="Editar abastecimento"
                              >
                                <Edit2 className="h-[14px] w-[14px] stroke-[1.8]" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedFueling(fueling)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:ring-offset-1"
                                title="Visualizar detalhes"
                                aria-label="Visualizar abastecimento"
                              >
                                <Eye className="h-[14px] w-[14px] stroke-[1.8]" />
                              </button>
                              <button
                                type="button"
                                onClick={() => abrirConfirmacaoExclusao(fueling)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                                title="Excluir abastecimento"
                                aria-label="Excluir abastecimento"
                              >
                                <Trash2 className="h-[14px] w-[14px] stroke-[1.8]" />
                              </button>
                            </div>
                          </div>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </ModernTable>

            {pageRange.total > 0 ? (
              <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[13px] font-medium text-slate-500">
                  Mostrando{' '}
                  <span className="font-semibold text-slate-700">
                    {pageRange.start}-{pageRange.end}
                  </span>{' '}
                  de{' '}
                  <span className="font-semibold text-slate-700">{pageRange.total}</span>{' '}
                  resultados
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 px-0.5">
                    {getPageNumbers().map((pageOrDots, idx) =>
                      pageOrDots === 'ellipsis' ? (
                        <span
                          key={`dots-${idx}`}
                          className="inline-flex h-9 w-8 items-center justify-center text-[13px] font-semibold text-slate-400"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={pageOrDots}
                          type="button"
                          onClick={() => setCurrentPage(pageOrDots)}
                          disabled={submitting}
                          className={`inline-flex h-9 w-8 cursor-pointer items-center justify-center rounded-lg text-[13px] font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 ${
                            currentPage === pageOrDots
                              ? 'bg-blue-600 text-white shadow-[0_2px_12px_-4px_rgba(37,99,235,0.7)]'
                              : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                          }`}
                        >
                          {pageOrDots}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isFormModalOpen && (
        <Modal
          onClose={closeFormModal}
        title={
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white shadow-[0_6px_16px_-8px_rgba(249,115,22,0.55)]">
              <Fuel className="h-5 w-5 stroke-[1.9]" />
            </div>
            <div>
              <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                {editingFueling ? 'Editar Abastecimento' : 'Registrar Abastecimento'}
              </div>
              <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                Preencha os campos abaixo para{' '}
                {editingFueling ? 'atualizar o registro' : 'criar um novo abastecimento'}
              </div>
            </div>
          </div>
        }
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
            <button
              type="button"
              onClick={closeFormModal}
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 disabled:pointer-events-none disabled:opacity-60"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="fueling-form"
              disabled={submitting}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-blue-600 via-blue-600 to-blue-700 px-4.5 text-[13px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)] transition-all hover:from-blue-700 hover:via-blue-700 hover:to-blue-800 hover:shadow-[0_8px_22px_-6px_rgba(37,99,235,0.65)] focus:outline-none focus:ring-4 focus:ring-blue-500/25 disabled:pointer-events-none disabled:opacity-60 disabled:shadow-none"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin stroke-[2]" />
                  Salvando...
                </>
              ) : editingFueling ? (
                'Salvar alterações'
              ) : (
                'Registrar abastecimento'
              )}
            </button>
          </div>
        }
      >
        <form id="fueling-form" onSubmit={saveFueling} className="space-y-5" noValidate>
          <div>
            <div className="mb-2 flex items-center gap-2">
              <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-sky-500 to-blue-600" />
              <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">
                Dados do abastecimento
              </h3>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1">
                      <Car className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Veículo
                    </span>
                  }
                  htmlFor="fueling-vehicle"
                  required
                  error={formErrors.veiculoId}
                >
                  <Select
                    id="fueling-vehicle"
                    value={formState.veiculoId}
                    onChange={(e) => handleFormChange('veiculoId', e.target.value)}
                  >
                    <option value="">Selecione um veículo</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.brand}
                        {vehicle.license_plate ? ` (${vehicle.license_plate})` : ''}
                      </option>
                    ))}
                  </Select>
                </FieldWrapper>

                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Motorista
                    </span>
                  }
                  htmlFor="fueling-employee"
                  required
                  error={formErrors.motoristaId}
                >
                  <Select
                    id="fueling-employee"
                    value={formState.motoristaId}
                    onChange={(e) => handleFormChange('motoristaId', e.target.value)}
                  >
                    <option value="">Selecione um motorista</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name}
                      </option>
                    ))}
                  </Select>
                </FieldWrapper>

                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Data
                    </span>
                  }
                  htmlFor="fueling-date"
                  required
                  error={formErrors.data}
                >
                  <Input
                    id="fueling-date"
                    type="date"
                    value={formState.data}
                    onChange={(e) => handleFormChange('data', e.target.value)}
                  />
                </FieldWrapper>

                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1">
                      <Droplets className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Combustível
                    </span>
                  }
                  htmlFor="fueling-type"
                  required
                  error={formErrors.tipoCombustivel}
                >
                  <Select
                    id="fueling-type"
                    value={formState.tipoCombustivel}
                    onChange={(e) => handleFormChange('tipoCombustivel', e.target.value)}
                  >
                    <option value="">Selecione o combustível</option>
                    {Object.keys(PRECOS_COMBUSTIVEL).map((tipo) => (
                      <option key={tipo} value={tipo}>
                        {tipo} — {formatCurrency(PRECOS_COMBUSTIVEL[tipo as TipoCombustivel])}/L
                      </option>
                    ))}
                  </Select>
                </FieldWrapper>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center gap-2">
              <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-green-600" />
              <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-green-700">
                Valores e medições
              </h3>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1">
                      <Droplets className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Quantidade (Litros)
                    </span>
                  }
                  htmlFor="fueling-amount"
                  required
                  error={formErrors.quantidade}
                >
                  <Input
                    id="fueling-amount"
                    type="number"
                    min="0"
                    step="0.1"
                    value={formState.quantidade || ''}
                    placeholder="Ex: 40.5"
                    onChange={(e) => handleFormChange('quantidade', Number(e.target.value))}
                  />
                </FieldWrapper>

                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1">
                      <DollarSign className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Valor Total
                    </span>
                  }
                  htmlFor="fueling-total"
                  required
                  error={formErrors.valorTotal}
                >
                  <Input
                    id="fueling-total"
                    type="text"
                    value={formState.valorTotal ? formatCurrency(formState.valorTotal) : ''}
                    readOnly
                    className="!bg-slate-50 !cursor-not-allowed"
                  />
                </FieldWrapper>

                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1">
                      <Gauge className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Quilometragem Atual
                    </span>
                  }
                  htmlFor="fueling-odometer"
                  required
                  error={formErrors.kmAtual}
                >
                  <Input
                    id="fueling-odometer"
                    type="number"
                    min="0"
                    max="2147483647"
                    value={formState.kmAtual || ''}
                    placeholder="Ex: 58400"
                    onChange={(e) => handleFormChange('kmAtual', Number(e.target.value))}
                  />
                </FieldWrapper>

                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1">
                      <Factory className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Fornecedor
                    </span>
                  }
                  htmlFor="fueling-provider"
                  required
                  error={formErrors.providerId}
                >
                  <Select
                    id="fueling-provider"
                    value={formState.providerId}
                    onChange={(e) => handleFormChange('providerId', e.target.value)}
                  >
                    <option value="">Selecione um fornecedor</option>
                    {providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </Select>
                </FieldWrapper>
              </div>
            </div>
          </div>
        </form>
      </Modal>
      )}

      {selectedFueling && (
        <Modal
          onClose={closeDetailsModal}
        title={
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-500 to-indigo-700 text-white shadow-[0_6px_16px_-8px_rgba(99,102,241,0.55)]">
              <Eye className="h-5 w-5 stroke-[1.9]" />
            </div>
            <div>
              <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                Detalhes do Abastecimento
              </div>
              <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                Registro completo do abastecimento selecionado
              </div>
            </div>
          </div>
        }
      >
        {selectedFueling && (
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-sky-500 to-blue-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">
                  Informações principais
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      Veículo
                    </p>
                    <p className="text-[14.5px] font-semibold text-slate-900">
                      {vehicleMap.get(selectedFueling.vehicle_id)?.brand ||
                        `Veículo #${selectedFueling.vehicle_id}`}
                    </p>
                    <p className="text-[12px] font-medium text-slate-500">
                      Placa: {vehicleMap.get(selectedFueling.vehicle_id)?.license_plate || '-'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      Motorista
                    </p>
                    <p className="text-[14.5px] font-semibold text-slate-900">
                      {employeeMap.get(selectedFueling.employee_id)?.name ||
                        `Motorista #${selectedFueling.employee_id}`}
                    </p>
                    <p className="text-[12px] font-medium text-slate-500">
                      CPF: {employeeMap.get(selectedFueling.employee_id)?.cpf || '-'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      Status
                    </p>
                    <div className="mt-0.5 inline-flex">{renderStatusBadge(selectedFueling.status)}</div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      Data / Horário
                    </p>
                    <p className="text-[14.5px] font-semibold text-slate-900 tabular-nums">
                      {formatDateTime(selectedFueling.fueling_time)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-green-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-green-700">
                  Financeiro & medição
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1">
                    <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      Combustível
                    </p>
                    <p className="text-[14.5px] font-semibold text-slate-900">
                      {selectedFueling.fuel_type}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      Quantidade
                    </p>
                    <p className="text-[14.5px] font-semibold text-slate-900 tabular-nums">
                      {selectedFueling.amount}L
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      Valor Total
                    </p>
                    <p className="text-[16px] font-bold text-slate-900 tabular-nums">
                      {formatCurrency(selectedFueling.total_cost)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      Quilometragem
                    </p>
                    <p className="text-[14.5px] font-semibold text-slate-900 tabular-nums">
                      {selectedFueling.odometer_reading
                        ? `${selectedFueling.odometer_reading.toLocaleString('pt-BR')} km`
                        : '-'}
                    </p>
                  </div>
                  <div className="md:col-span-2 space-y-1">
                    <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      Fornecedor
                    </p>
                    <p className="text-[14.5px] font-semibold text-slate-900">
                      {providerMap.get(selectedFueling.provider_id)?.name ||
                        `Fornecedor #${selectedFueling.provider_id}`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end sm:items-center sm:flex-wrap">
              <button
                type="button"
                onClick={closeDetailsModal}
                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={() => void abrirConfirmacaoExclusao(selectedFueling)}
                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-4 text-[13px] font-semibold text-rose-700 transition-all hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
              >
                <Trash2 className="h-4 w-4 stroke-[1.8]" />
                Excluir
              </button>
              <button
                type="button"
                onClick={() => openEditModal(selectedFueling)}
                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-4 text-[13px] font-semibold text-amber-700 transition-all hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              >
                <Edit2 className="h-4 w-4 stroke-[1.8]" />
                Editar
              </button>
              {selectedFueling.status === 'pendente' && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      void handleStatusChange(selectedFueling, 'rejeitado');
                      closeDetailsModal();
                    }}
                    className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-4 text-[13px] font-semibold text-rose-700 transition-all hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                  >
                    <CircleX className="h-4 w-4 stroke-[2]" />
                    Rejeitar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void handleStatusChange(selectedFueling, 'aprovado');
                      closeDetailsModal();
                    }}
                    className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 text-[13px] font-semibold text-emerald-700 transition-all hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  >
                    <CircleCheck className="h-4 w-4 stroke-[2]" />
                    Aprovar
                  </button>
                </>
              )}
              {selectedFueling.status === 'aprovado' && (
                <button
                  type="button"
                  onClick={() => {
                    openFinishModal(selectedFueling.id);
                    closeDetailsModal();
                  }}
                  className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-blue-600 via-blue-600 to-blue-700 px-4.5 text-[13px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)] transition-all hover:from-blue-700 hover:via-blue-700 hover:to-blue-800 hover:shadow-[0_8px_22px_-6px_rgba(37,99,235,0.65)] focus:outline-none focus:ring-4 focus:ring-blue-500/25"
                >
                  <CheckCircle2 className="h-4 w-4 stroke-[2]" />
                  Finalizar
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
      )}

      {isFinishModalOpen && (
        <Modal
          onClose={closeFinishModal}
        title={
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 via-sky-500 to-cyan-600 text-white shadow-[0_6px_16px_-8px_rgba(14,165,233,0.55)]">
              <ImageIcon className="h-5 w-5 stroke-[1.9]" />
            </div>
            <div>
              <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                Finalizar Abastecimento
              </div>
              <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                Envie a foto do boleto para concluir
              </div>
            </div>
          </div>
        }
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
            <button
              type="button"
              onClick={closeFinishModal}
              disabled={submitting}
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 disabled:pointer-events-none disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void handleFinishFueling()}
              disabled={!boletoFile || submitting}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-blue-600 via-blue-600 to-blue-700 px-4.5 text-[13px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)] transition-all hover:from-blue-700 hover:via-blue-700 hover:to-blue-800 hover:shadow-[0_8px_22px_-6px_rgba(37,99,235,0.65)] focus:outline-none focus:ring-4 focus:ring-blue-500/25 disabled:pointer-events-none disabled:opacity-60 disabled:shadow-none"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin stroke-[2]" />
                  Finalizando...
                </>
              ) : (
                'Finalizar abastecimento'
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 ring-1 ring-inset ring-blue-200">
                <ImageIcon className="h-4 w-4 stroke-[1.9] text-blue-700" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-blue-800 leading-tight">
                  Envie a foto do comprovante
                </div>
                <p className="mt-1 text-[12.5px] leading-relaxed font-medium text-blue-700/90">
                  Ao confirmar, este abastecimento será movido para o{' '}
                  <span className="font-bold">histórico</span> e não aparecerá mais na lista principal.
                </p>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="fueling-boleto">
              <span className="inline-flex items-center gap-1">
                <ImageIcon className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                Foto do boleto / comprovante
                <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span>
              </span>
            </Label>
            <input
              id="fueling-boleto"
              type="file"
              accept="image/*"
              onChange={(e) => setBoletoFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-[13px] text-slate-700 file:mr-3 file:inline-flex file:h-9 file:items-center file:rounded-lg file:border file:border-slate-200 file:bg-white file:px-3 file:text-[12px] file:font-semibold file:text-slate-700 file:transition-all hover:file:bg-slate-50 focus:outline-none"
            />
          </div>

          {boletoPreview && (
            <div className="rounded-2xl border border-slate-200 bg-white p-2.5">
              <div className="mb-2 flex items-center justify-between px-1">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  Pré-visualização
                </span>
                <span className="text-[11px] font-semibold text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 stroke-[2]" /> Arquivo carregado
                </span>
              </div>
              <img
                src={boletoPreview}
                alt="Pré-visualização do boleto"
                className="w-full max-h-80 rounded-xl border border-slate-200 object-contain bg-slate-50"
              />
            </div>
          )}
        </div>
      </Modal>
      )}

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        title={
          <span className="inline-flex items-center gap-2">
            <Trash2 className="h-5 w-5 stroke-[1.8]" />
            Excluir abastecimento?
          </span>
        }
        description={
          fuelingParaExcluir ? (
            <div className="space-y-1">
              <p className="text-[13.5px] leading-relaxed text-slate-600">
                Tem certeza que deseja excluir este abastecimento?
              </p>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5">
                <p className="text-[12.5px] font-semibold text-slate-700">
                  Veículo:{' '}
                  {vehicleMap.get(fuelingParaExcluir.vehicle_id)?.brand ||
                    `#${fuelingParaExcluir.vehicle_id}`}
                </p>
                <p className="text-[12px] font-medium text-slate-500">
                  {vehicleMap.get(fuelingParaExcluir.vehicle_id)?.license_plate
                    ? `Placa ${vehicleMap.get(fuelingParaExcluir.vehicle_id)?.license_plate} · `
                    : ''}
                  {formatDate(fuelingParaExcluir.fueling_time)} ·{' '}
                  {formatCurrency(fuelingParaExcluir.total_cost)}
                </p>
              </div>
              <p className="pt-1 text-[12.5px] text-rose-600 font-medium">
                Esta ação é irreversível e não pode ser desfeita.
              </p>
            </div>
          ) : undefined
        }
        confirmLabel={
          <span className="inline-flex items-center gap-1.5">
            {deleteLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin stroke-[2]" />
                Excluindo...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 stroke-[1.8]" />
                Sim, excluir abastecimento
              </>
            )}
          </span>
        }
        cancelLabel="Cancelar"
        confirmVariant="danger"
        loading={deleteLoading}
        onConfirm={() => void confirmarExclusao()}
        onCancel={cancelarExclusao}
      />
    </div>
  );
};

export default Abastecimento;
